import difference from "lodash/difference";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { MessageType, messageTypeToAuthorMap } from "../../constants/ChatMessageTypes";

import ApiConstants from "../../constants/api/Public";
import { postMessageToParent } from "../Utils";
import { PostMessage } from "../../constants/chat";

/*eslint-disable*/
const {
    ON_READY,
    ON_NAVIGATE,
    ON_ASK_QUESTION,
    ON_MESSAGE,
    ON_RATED,
    ON_REJECTION,
    ON_REROUTE,
    ON_SOLVED,
    QUESTION_STATUS_EXPIRED: EXPIRED,
    QUESTION_STATUS_RESOLVED: RESOLVED,
    QUESTION_STATUS_UNRESOLVED: UNRESOLVED,
    ON_ERROR_EVENT,
} = ApiConstants;
let configInfo = {};
export function triggerReadyEvent({ config, response }) {
    configInfo = config;
    postMessageToParent(PostMessage.EVENT_LISTENER, {
        callbackName: ON_READY,
        data: {
            config,
            questions: get(response, "body.data.questions"),
            session: get(response, "body.data.session", false),
        },
    });
}

export function triggerNavigateEvent(location) {
    postMessageToParent(PostMessage.EVENT_LISTENER, {
        callbackName: ON_NAVIGATE,
        data: { path: `${location.pathname}${location.search}${location.hash}` },
    });
}

export function triggerAskQuestionEvent(question) {
    postMessageToParent(PostMessage.EVENT_LISTENER, {
        callbackName: ON_ASK_QUESTION,
        data: {
            questionUuid: question.uuid,
            questionText: question.body,
            dateCreated: question.dateCreated,
        },
    });
}

function triggerSolvedEvent({ prevQuestion, question }) {
    const hasBeenResolved = [RESOLVED, EXPIRED].includes(question.status) && prevQuestion.status === UNRESOLVED;
    if (hasBeenResolved) {
        postMessageToParent(PostMessage.EVENT_LISTENER, {
            callbackName: ON_SOLVED,
            data: {
                dateCreated: question.dateClosed,
                questionStatus: question.status,
                questionUuid: question.uuid,
                ...(configInfo.hasOwnProperty("includeChatTranscript") && configInfo["includeChatTranscript"]
                    ? {
                          chatTranscript: question.chatTranscriptAsJson,
                      }
                    : {}),
            },
        });
    }
}

function triggerRerouteEvents({ question, prevQuestion }) {
    const {
        lastUpdated,
        status,
        uuid,
        chatTranscriptAsJson,
        dateClosed,
        reroutingStatus: {
            rerouted,
            reroutedByExpert,
            reroutedByPoster,
            reroutedText,
            reroutedLabel,
            reroutedCode,
            rerouteType,
        } = {},
    } = question;
    const statusChangedToRerouted = rerouted && !prevQuestion.reroutingStatus.rerouted;
    const abusiveCustomers =
        rerouteType == null && status == "5" && dateClosed != null && prevQuestion.dateClosed == null;
    const rerouteTypeStatus = rerouteType && prevQuestion.reroutingStatus.rerouteType == null;
    if (statusChangedToRerouted || rerouteTypeStatus || abusiveCustomers) {
        postMessageToParent(PostMessage.EVENT_LISTENER, {
            callbackName: ON_REROUTE,
            data: {
                rerouteType: abusiveCustomers ? "FLAGGED_ABUSIVE_CUSTOMERS" : rerouteType,
                reroutedByExpert,
                reroutedByPoster,
                rerouteReason: reroutedText,
                rerouteReasonLabel: reroutedLabel,
                rerouteReasonCode: reroutedCode,
                dateCreated: lastUpdated,
                questionUuid: uuid,
                questionStatus: status,
                ...(configInfo.hasOwnProperty("includeChatTranscript") && configInfo["includeChatTranscript"]
                    ? {
                          chatTranscript: chatTranscriptAsJson,
                      }
                    : {}),
            },
        });
    }
}
function triggerRatedEvent({ question, response, prevResponse }) {
    if (isEmpty(response) || !response.happinessLastUpdated) {
        return;
    }
    const ratingChanged = response.customerHappiness !== prevResponse.customerHappiness;
    if (ratingChanged) {
        postMessageToParent(PostMessage.EVENT_LISTENER, {
            callbackName: ON_RATED,
            data: {
                sentiment: response.happiness,
                questionUuid: question.uuid,
                responseUuid: response.uuid,
                questionStatus: question.status,
                rating: response.customerHappiness,
                dateCreated: response.happinessLastUpdated,
            },
        });
    }
}

function triggerRejectionEvent({ question, message }) {
    postMessageToParent(PostMessage.EVENT_LISTENER, {
        callbackName: ON_REJECTION,
        data: {
            dateCreated: message.dateCreated,
            rerouteType: question.rerouteType,
            rejectionType: message.type,
            questionUuid: question.uuid,
            questionStatus: question.status,
        },
    });
}

export function triggerDefaultMessageEvent({ question, message, response }) {
    postMessageToParent(PostMessage.EVENT_LISTENER, {
        callbackName: ON_MESSAGE,
        data: {
            authorType: messageTypeToAuthorMap[message.type],
            dateCreated: message.dateCreated,
            messageUuid: message.uuid,
            messageText: message.messageText,
            messageTextHtml: message.messageTextHtml,
            messageType: message.type,
            questionUuid: question.uuid,
            questionStatus: question.status,
            responseUuid: response.uuid,
        },
    });
}

function triggerMessageEvents({ question, messages, prevMessages, response }) {
    const {
        QuestionRejectedByKeywordSystemMessage,
        QuestionRejectedByMetadataSystemMessage,
        QuestionRejectedByPredictiveRoutingSystemMessage,
        QuestionRejectedByDomainSystemMessage,
        QuestionRejectedByBudgetSystemMessage,
        QuestionRejectedByBlockedUser,
        QuestionRejectedByIpAddress,
    } = MessageType;
    const currentMessageIds = messages.map(message => message.id);
    const prevMessageIds = prevMessages.map(message => message.id);
    const newIds = difference(currentMessageIds, prevMessageIds);
    newIds.forEach(newId => {
        const newMessage = messages.find(message => message.id === newId);
        if (!newMessage.type) {
            return;
        }
        switch (newMessage.type) {
            case QuestionRejectedByKeywordSystemMessage:
            case QuestionRejectedByMetadataSystemMessage:
            case QuestionRejectedByPredictiveRoutingSystemMessage:
            case QuestionRejectedByDomainSystemMessage:
            case QuestionRejectedByBudgetSystemMessage:
            case QuestionRejectedByBlockedUser:
            case QuestionRejectedByIpAddress:
                triggerRejectionEvent({ question, message: newMessage });
                break;
            default:
                triggerDefaultMessageEvent({ question, message: newMessage, response });
        }
    });
}

export function triggerAppropriateEvents({ messages, question, response, prevMessages, prevQuestion, prevResponse }) {
    triggerMessageEvents({ messages, prevMessages, question, response });
    triggerRerouteEvents({ prevQuestion, question });
    triggerRatedEvent({ prevResponse, question, response });
    triggerSolvedEvent({ prevQuestion, question });
}

export function triggerOnErrorEvent(payload) {
    postMessageToParent(PostMessage.EVENT_LISTENER, {
        callbackName: ON_ERROR_EVENT,
        data: payload,
    });
}

export default {
    triggerAppropriateEvents,
    triggerAskQuestionEvent,
    triggerNavigateEvent,
    triggerReadyEvent,
    triggerDefaultMessageEvent,
    triggerOnErrorEvent
};
